import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'

interface AdminDateInputProps {
  id?: string
  value: string
  onChange: (value: string) => void
  includeTime?: boolean
  required?: boolean
  min?: string
  max?: string
  className?: string
}

const toDisplayValue = (value: string, includeTime: boolean) => {
  if (!value) return ''
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/)
  if (!match) return value

  const [, year, month, day, hour = '00', minute = '00'] = match
  return includeTime
    ? `${day}/${month}/${year} ${hour}:${minute}`
    : `${day}/${month}/${year}`
}

const toIsoValue = (value: string, includeTime: boolean) => {
  const trimmedValue = value.trim()
  const frenchPattern = includeTime
    ? /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})$/
    : /^(\d{2})\/(\d{2})\/(\d{4})$/
  const isoPattern = includeTime
    ? /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})$/
    : /^(\d{4})-(\d{2})-(\d{2})$/
  const frenchMatch = trimmedValue.match(frenchPattern)
  const isoMatch = trimmedValue.match(isoPattern)

  if (!frenchMatch && !isoMatch) return null

  const [, first, second, third, hour = '00', minute = '00'] = frenchMatch ?? isoMatch!
  const [year, month, day] = frenchMatch
    ? [third, second, first]
    : [first, second, third]
  const date = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute))
  const isValid = (
    date.getFullYear() === Number(year)
    && date.getMonth() === Number(month) - 1
    && date.getDate() === Number(day)
    && date.getHours() === Number(hour)
    && date.getMinutes() === Number(minute)
  )

  if (!isValid) return null
  return includeTime
    ? `${year}-${month}-${day}T${hour}:${minute}`
    : `${year}-${month}-${day}`
}

const dateKey = (date: Date) => `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
const localDate = (value: string) => new Date(`${value.slice(0, 10)}T12:00:00`)
const months = Array.from({ length: 12 }, (_, month) => new Date(2024, month, 1).toLocaleString('fr-FR', { month: 'long' }))

export default function AdminDateInput({ id, value, onChange, includeTime = false, required = false, min, max, className = '' }: AdminDateInputProps) {
  const [displayValue, setDisplayValue] = useState(() => toDisplayValue(value, includeTime))
  const [open, setOpen] = useState(false)
  const [cursor, setCursor] = useState(() => dateKey(new Date()))
  const rootRef = useRef<HTMLDivElement>(null)
  const textInputRef = useRef<HTMLInputElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)
  const calendarId = useId()
  const today = dateKey(new Date())
  const displayedMonth = localDate(cursor)
  const year = displayedMonth.getFullYear()
  const month = displayedMonth.getMonth()
  const time = value.slice(11, 16) || '00:00'
  const dayAllowed = (day: string) => (!min || day >= min.slice(0, 10)) && (!max || day <= max.slice(0, 10))
  const validationMessage = (text: string) => {
    const iso = toIsoValue(text, includeTime)
    if (text.trim() && !iso) return includeTime ? 'Saisissez une date et une heure valides au format JJ/MM/AAAA HH:mm.' : 'Saisissez une date valide au format JJ/MM/AAAA.'
    if (iso && min && iso < min) return `La date doit être postérieure ou égale au ${toDisplayValue(min, includeTime)}.`
    if (iso && max && iso > max) return `La date doit être antérieure ou égale au ${toDisplayValue(max, includeTime)}.`
    return ''
  }
  useEffect(() => { setDisplayValue(toDisplayValue(value, includeTime)) }, [value, includeTime])
  // Revalidate typed text when the bounds change too.
  useEffect(() => {
    textInputRef.current?.setCustomValidity(validationMessage(displayValue))
  })
  useLayoutEffect(() => {
    const calendar = calendarRef.current
    const button = buttonRef.current
    if (!open || !calendar || !button) return

    // La couche supérieure évite le découpage par les modales défilantes.
    calendar.showPopover()
    const positionCalendar = () => {
      const anchor = button.getBoundingClientRect()
      const margin = 12
      const gap = 8
      const viewportWidth = document.documentElement.clientWidth
      const viewportHeight = window.innerHeight
      calendar.style.maxHeight = `${Math.max(0, viewportHeight - margin * 2)}px`
      const bounds = calendar.getBoundingClientRect()
      const below = viewportHeight - anchor.bottom - gap - margin
      const above = anchor.top - gap - margin
      const top = below >= bounds.height || below >= above
        ? anchor.bottom + gap
        : anchor.top - gap - bounds.height
      calendar.style.left = `${Math.max(margin, Math.min(anchor.right - bounds.width, viewportWidth - bounds.width - margin))}px`
      calendar.style.top = `${Math.max(margin, Math.min(top, viewportHeight - bounds.height - margin))}px`
    }
    positionCalendar()
    const observer = new ResizeObserver(positionCalendar)
    observer.observe(calendar)
    window.addEventListener('resize', positionCalendar)
    window.addEventListener('scroll', positionCalendar, true)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', positionCalendar)
      window.removeEventListener('scroll', positionCalendar, true)
      if (calendar.isConnected) calendar.hidePopover()
    }
  }, [open])
  useEffect(() => {
    if (!open) return
    const close = () => { setOpen(false); buttonRef.current?.focus() }
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopImmediatePropagation()
        close()
      }
    }
    const outside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false)
        // A first click on the modal backdrop only dismisses the calendar.
        if (event.target instanceof HTMLElement && event.target.classList.contains('modal-backdrop')) {
          event.stopPropagation()
          buttonRef.current?.focus()
        }
      }
    }
    document.addEventListener('keydown', keydown, true)
    document.addEventListener('pointerdown', outside, true)
    return () => {
      document.removeEventListener('keydown', keydown, true)
      document.removeEventListener('pointerdown', outside, true)
    }
  }, [open])
  useEffect(() => {
    if (open) rootRef.current?.querySelector<HTMLButtonElement>(`[data-day="${cursor}"]`)?.focus()
  }, [open, cursor])

  const update = (text: string) => {
    setDisplayValue(text)
    const error = validationMessage(text)
    textInputRef.current?.setCustomValidity(error)
    const iso = toIsoValue(text, includeTime)
    if (!error && iso) onChange(iso)
    else if (!text.trim()) onChange('')
  }
  const selectDay = (day: string) => {
    let next = includeTime ? `${day}T${time}` : day
    if (min && next < min) next = min
    if (max && next > max) next = max
    update(toDisplayValue(next, includeTime))
    if (!includeTime) { setOpen(false); buttonRef.current?.focus() }
  }
  const moveCursor = (date: Date) => {
    if (date.getFullYear() < 100 || date.getFullYear() > 9999) return
    let next = dateKey(date)
    if (min && next < min.slice(0, 10)) next = min.slice(0, 10)
    if (max && next > max.slice(0, 10)) next = max.slice(0, 10)
    setCursor(next)
  }
  const firstOffset = (new Date(year, month, 1).getDay() + 6) % 7
  const dayCount = new Date(year, month + 1, 0).getDate()

  return (
    <div ref={rootRef} className={`admin-date ${className}`} onBlur={event => {
      if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false)
    }}>
      <div className="flex min-w-0 items-stretch gap-2">
        <input ref={textInputRef} id={id} type="text" inputMode="numeric" required={required}
          placeholder={includeTime ? 'JJ/MM/AAAA HH:mm' : 'JJ/MM/AAAA'} value={displayValue}
          onChange={event => update(event.target.value)} onBlur={() => {
            const iso = toIsoValue(displayValue, includeTime)
            if (iso) setDisplayValue(toDisplayValue(iso, includeTime))
          }} className="input-field min-w-0 flex-1" />
        <button ref={buttonRef} type="button" className="admin-date-trigger" aria-expanded={open} aria-controls={calendarId}
          aria-label={includeTime ? 'Choisir la date et l’heure dans le calendrier' : 'Choisir la date dans le calendrier'}
          onClick={() => {
            if (!open) moveCursor(localDate(value || today))
            setOpen(!open)
          }}><CalendarDays className="h-5 w-5 pointer-events-none" aria-hidden="true" /></button>
      </div>
      {open && <div ref={calendarRef} id={calendarId} popover="manual" className="admin-calendar" role="group" aria-label="Calendrier">
        <div className="admin-calendar-navigation">
          <button type="button" aria-label="Mois précédent" onClick={() => moveCursor(new Date(year, month - 1, 1))}><ChevronLeft aria-hidden="true" /></button>
          <select aria-label="Mois" value={month} onChange={event => moveCursor(new Date(year, Number(event.target.value), 1))}>
            {months.map((label, index) => <option key={label} value={index}>{label}</option>)}
          </select>
          <input aria-label="Année" type="number" min={100} max={9999} defaultValue={year} key={year}
            onBlur={event => { const next = Number(event.target.value); if (next >= 100 && next <= 9999) moveCursor(new Date(next, month, 1)); else event.target.value = String(year) }}
            onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); const next = Number(event.currentTarget.value); if (next >= 100 && next <= 9999) moveCursor(new Date(next, month, 1)) } }} />
          <button type="button" aria-label="Mois suivant" onClick={() => moveCursor(new Date(year, month + 1, 1))}><ChevronRight aria-hidden="true" /></button>
        </div>
        <div className="admin-calendar-grid" role="group" aria-label={`${months[month]} ${year}`}>
          {['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'].map(day => <span className="admin-calendar-weekday" key={day}>{day}</span>)}
          {Array.from({ length: firstOffset }, (_, index) => <span key={`empty-${index}`} />)}
          {Array.from({ length: dayCount }, (_, index) => {
            const day = `${String(year).padStart(4, '0')}-${String(month + 1).padStart(2, '0')}-${String(index + 1).padStart(2, '0')}`
            return <button key={day} type="button" data-day={day} disabled={!dayAllowed(day)} tabIndex={day === cursor ? 0 : -1}
              aria-label={localDate(day).toLocaleDateString('fr-FR', { dateStyle: 'full' })} aria-pressed={value.slice(0, 10) === day}
              aria-current={day === today ? 'date' : undefined} onClick={() => selectDay(day)}
              onKeyDown={event => {
                const offsets: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }
                const next = localDate(day)
                if (event.key in offsets) next.setDate(next.getDate() + offsets[event.key])
                else if (event.key === 'Home') next.setDate(next.getDate() - (next.getDay() + 6) % 7)
                else if (event.key === 'End') next.setDate(next.getDate() + 6 - (next.getDay() + 6) % 7)
                else if (event.key === 'PageUp' || event.key === 'PageDown') next.setMonth(next.getMonth() + (event.key === 'PageUp' ? -1 : 1), 1)
                else return
                event.preventDefault(); moveCursor(next)
              }}>{index + 1}</button>
          })}
        </div>
        {includeTime && <label className="admin-calendar-time">Heure
          <input type="time" aria-label="Heure" value={time} disabled={!value}
            min={min?.slice(0, 10) === value.slice(0, 10) ? min.slice(11, 16) : undefined}
            max={max?.slice(0, 10) === value.slice(0, 10) ? max.slice(11, 16) : undefined}
            onChange={event => { if (event.target.value) update(toDisplayValue(`${value.slice(0, 10)}T${event.target.value}`, true)) }} />
        </label>}
        <div className="admin-calendar-footer">
          <button type="button" disabled={!dayAllowed(today)} onClick={() => { moveCursor(localDate(today)); selectDay(today) }}>Aujourd’hui</button>
          <button type="button" onClick={() => { setOpen(false); buttonRef.current?.focus() }}>Fermer</button>
        </div>
      </div>}
    </div>
  )
}
